import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const results = [
  {
    metric: '3x',
    label: 'More Walk-in Customers',
    story: 'A local cafe went from 20 to 60+ daily walk-ins after implementing our Instagram growth system.',
    business: 'Cafe in Mumbai',
  },
  {
    metric: '500%',
    label: 'Increase in Orders',
    story: 'A cloud kitchen saw their online orders multiply 5x within 90 days of strategic Reels content.',
    business: 'Cloud Kitchen',
  },
  {
    metric: '10K+',
    label: 'Engaged Followers',
    story: 'A restaurant built a loyal local following that translates to packed tables every weekend.',
    business: 'Restaurant Chain',
  },
];

export const ResultsSection = () => {
  return (
    <section
      id="results"
      data-testid="results-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Left: Image + context */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <span className="font-accent text-slate-400 mb-4 block" data-testid="results-label">
              Results
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-tight mb-6"
              data-testid="results-headline"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Real Growth.{' '}
              <span className="font-medium">Real Customers.</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-8">
              We measure success by one thing: customers walking through your
              door. Here's what our growth system delivers.
            </p>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1566651787053-7574c08b1a9f?w=600&h=400&fit=crop"
                alt="Business owner using smartphone"
                className="w-full h-64 object-cover"
                data-testid="results-image"
              />
            </div>
          </motion.div>

          {/* Right: Results cards */}
          <div className="md:col-span-7 flex flex-col gap-6">
            {results.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="bg-slate-50 border border-slate-100 rounded-2xl p-8 hover:border-slate-200 transition-colors group"
                data-testid={`result-card-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span
                      className="text-4xl md:text-5xl font-light text-slate-900 block"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {item.metric}
                    </span>
                    <span className="text-sm font-medium text-slate-700 mt-1 block">
                      {item.label}
                    </span>
                  </div>
                  <ArrowUpRight
                    size={20}
                    className="text-slate-300 group-hover:text-slate-500 transition-colors"
                  />
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {item.story}
                </p>
                <span className="font-accent text-slate-300 mt-4 block text-[10px]">
                  {item.business}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
